import { api } from "./api";
import type {
  AppState,
  Marker,
  Point,
  ProjectMeta,
  Shape,
  ShapeKind,
  StatePayload,
} from "./types";

/* ------------------------------ helpers ------------------------------ */

export function uid(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

export function nowISO(): string {
  return new Date().toISOString();
}

export function todayISO(): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/Berlin",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

/* --------------------------- store internals --------------------------- */

export type SaveStatus = "idle" | "pending" | "ok" | "error";

type ChangeListener = () => void;
type StatusListener = (status: SaveStatus, detail: string) => void;

let current: AppState | null = null;
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let lastSaveError: string | null = null;

const changeListeners = new Set<ChangeListener>();
const statusListeners = new Set<StatusListener>();

export function onChange(fn: ChangeListener): () => void {
  changeListeners.add(fn);
  return () => changeListeners.delete(fn);
}
export function onSaveStatus(fn: StatusListener): () => void {
  statusListeners.add(fn);
  return () => statusListeners.delete(fn);
}
function emit(): void {
  for (const fn of changeListeners) fn();
}
function emitStatus(status: SaveStatus, detail: string): void {
  for (const fn of statusListeners) fn(status, detail);
}

/* ----------------------------- accessors ----------------------------- */

export function getState(): AppState {
  if (!current) throw new Error("Kein Projekt geladen.");
  return current;
}
export function hasState(): boolean {
  return current !== null;
}
export function getProject(): ProjectMeta {
  return getState().project;
}

/* --------------------------- load / migrate --------------------------- */

export function loadState(appState: AppState): void {
  current = normalize(appState);
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  lastSaveError = null;
  emit();
  emitStatus("ok", "Geladen");
}

export function unload(): void {
  current = null;
}

function normalize(appState: AppState): AppState {
  return {
    project: appState.project,
    scale: appState.scale ?? appState.project.scale ?? { pxPerMeter: 31.5, calibrated: false },
    nextNum: Number.isFinite(appState.nextNum) ? appState.nextNum : 1,
    markers: (appState.markers ?? []).map((m) => normalizeMarker(m as unknown as Record<string, unknown>)),
    shapes: (appState.shapes ?? []).map((z) => normalizeShape(z as unknown as Record<string, unknown>)),
  };
}

function normalizeImages(raw: Record<string, unknown>): string[] {
  if (Array.isArray(raw.images)) return (raw.images as unknown[]).filter((x): x is string => !!x);
  if (typeof raw.imageFile === "string" && raw.imageFile) return [raw.imageFile];
  return [];
}

function normalizeMarker(raw: Record<string, unknown>): Marker {
  return {
    id: (raw.id as string) || uid(),
    num: Number(raw.num) || 0,
    category: (raw.category as string) || (raw.cat as string) || "sonstiges",
    x: Number(raw.x) || 0,
    y: Number(raw.y) || 0,
    label: (raw.label as string) || "",
    note: (raw.note as string) || "",
    images: normalizeImages(raw),
    imageUrls: Array.isArray(raw.imageUrls) ? (raw.imageUrls as string[]) : undefined,
    locked: !!raw.locked,
    createdAt: (raw.createdAt as string) || nowISO(),
  };
}

function normalizeShape(raw: Record<string, unknown>): Shape {
  const kind: ShapeKind =
    raw.kind === "object" || raw.kind === "line" ? raw.kind : "area";
  const pts = Array.isArray(raw.points) ? (raw.points as Point[]) : [];
  return {
    id: (raw.id as string) || uid(),
    kind,
    category: (raw.category as string) || (raw.cat as string) || "",
    points: pts.map((p) => ({ x: Number(p.x) || 0, y: Number(p.y) || 0 })),
    closed: !!raw.closed,
    name: (raw.name as string) || "",
    note: (raw.note as string) || "",
    images: normalizeImages(raw),
    imageUrls: Array.isArray(raw.imageUrls) ? (raw.imageUrls as string[]) : undefined,
    locked: !!raw.locked,
    createdAt: (raw.createdAt as string) || nowISO(),
  };
}

/**
 * Accepts either this app's export format or the legacy single-foyer prototype
 * format (`zones` array, `cat` fields) and returns markers/shapes/scale/nextNum.
 */
export function migrateImport(rawInput: unknown): Pick<
  AppState,
  "markers" | "shapes" | "scale" | "nextNum"
> {
  const raw = (rawInput ?? {}) as Record<string, unknown>;
  const rawShapes: unknown[] = Array.isArray(raw.shapes)
    ? (raw.shapes as unknown[])
    : [];
  const legacyZones: unknown[] = Array.isArray(raw.zones)
    ? (raw.zones as unknown[])
    : [];
  const markers = (Array.isArray(raw.markers) ? (raw.markers as unknown[]) : []).map(
    (m) => normalizeMarker(m as Record<string, unknown>)
  );
  const shapes = [...rawShapes, ...legacyZones].map((s) =>
    normalizeShape(s as Record<string, unknown>)
  );
  const scaleRaw = (raw.scale ?? {}) as Record<string, unknown>;
  const scale = {
    pxPerMeter: Number(scaleRaw.pxPerMeter) > 0 ? Number(scaleRaw.pxPerMeter) : 31.5,
    calibrated: !!scaleRaw.calibrated,
  };
  const maxNum = markers.reduce((acc, m) => Math.max(acc, m.num), 0);
  const nextNum = Math.max(Number(raw.nextNum) || 0, maxNum + 1);
  return { markers, shapes, scale, nextNum };
}

/* ------------------------------- CRUD ------------------------------- */

export function addMarker(category: string, x: number, y: number): Marker {
  const s = getState();
  const marker: Marker = {
    id: uid(),
    num: s.nextNum++,
    category,
    x,
    y,
    label: "",
    note: "",
    images: [],
    locked: false,
    createdAt: nowISO(),
  };
  s.markers.push(marker);
  changed();
  return marker;
}

/**
 * Gap-free display numbers (1..N) for the current markers, in creation order.
 * `marker.num` itself is only an internal creation-order key (never reused,
 * so it develops gaps once markers are deleted) — UI/print always show the
 * value from this map instead.
 */
export function markerDisplayNums(): Map<string, number> {
  const sorted = [...getState().markers].sort((a, b) => a.num - b.num);
  const map = new Map<string, number>();
  sorted.forEach((m, i) => map.set(m.id, i + 1));
  return map;
}

/**
 * Gap-free 1..N index of each marker within its own category (creation
 * order) — used to tell unnamed markers of the same category apart on
 * printed labels, e.g. "Mülleimer 1", "Mülleimer 2".
 */
export function markerCategoryNums(): Map<string, number> {
  const sorted = [...getState().markers].sort((a, b) => a.num - b.num);
  const perCat = new Map<string, number>();
  const map = new Map<string, number>();
  for (const m of sorted) {
    const next = (perCat.get(m.category) ?? 0) + 1;
    perCat.set(m.category, next);
    map.set(m.id, next);
  }
  return map;
}

export function updateMarker(id: string, patch: Partial<Marker>): void {
  const m = getState().markers.find((x) => x.id === id);
  if (!m) return;
  Object.assign(m, patch);
  changed();
}

export function deleteMarker(id: string): Marker | null {
  const s = getState();
  const idx = s.markers.findIndex((m) => m.id === id);
  if (idx < 0) return null;
  const [removed] = s.markers.splice(idx, 1);
  changed();
  return removed ?? null;
}

export function restoreMarker(marker: Marker): void {
  getState().markers.push(marker);
  changed();
}

export function addShape(
  kind: ShapeKind,
  category: string,
  points: Point[],
  closed: boolean
): Shape {
  const shape: Shape = {
    id: uid(),
    kind,
    category,
    points,
    closed,
    name: "",
    note: "",
    images: [],
    locked: false,
    createdAt: nowISO(),
  };
  getState().shapes.push(shape);
  changed();
  return shape;
}

export function updateShape(id: string, patch: Partial<Shape>): void {
  const shape = getState().shapes.find((x) => x.id === id);
  if (!shape) return;
  Object.assign(shape, patch);
  changed();
}

export function deleteShape(id: string): Shape | null {
  const s = getState();
  const idx = s.shapes.findIndex((z) => z.id === id);
  if (idx < 0) return null;
  const [removed] = s.shapes.splice(idx, 1);
  changed();
  return removed ?? null;
}

export function restoreShape(shape: Shape): void {
  getState().shapes.push(shape);
  changed();
}

export function setScale(pxPerMeter: number, calibrated: boolean): void {
  const s = getState();
  s.scale = { pxPerMeter, calibrated };
  changed();
}

export function replaceWorkingSet(
  data: Pick<AppState, "markers" | "shapes" | "scale" | "nextNum">
): void {
  const s = getState();
  s.markers = data.markers;
  s.shapes = data.shapes;
  s.scale = data.scale;
  s.nextNum = data.nextNum;
  changed();
}

/* ---------------------------- persistence ---------------------------- */

export function toPayload(): StatePayload {
  const s = getState();
  return {
    markers: s.markers,
    shapes: s.shapes,
    scale: s.scale,
    nextNum: s.nextNum,
  };
}

function changed(): void {
  emit();
  scheduleSave();
}

/** Commit direct in-place mutations (e.g. live drag of a marker/vertex). */
export function touch(): void {
  changed();
}

/** Re-render subscribers without scheduling a save (used mid-drag). */
export function refresh(): void {
  emit();
}

const SAVE_DEBOUNCE_MS = 1400;

export function scheduleSave(): void {
  if (!current) return;
  emitStatus("pending", "Änderungen…");
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    void saveNow();
  }, SAVE_DEBOUNCE_MS);
}

export async function saveNow(): Promise<void> {
  if (!current) return;
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  const project = current.project;
  try {
    const updated = await api.saveState(project.id, toPayload());
    // Keep server-confirmed timestamps without clobbering local edits mid-flight.
    current.project.updatedAt = updated.project.updatedAt;
    lastSaveError = null;
    emitStatus("ok", `Gesichert ${timeShort()}`);
  } catch (err) {
    lastSaveError = err instanceof Error ? err.message : String(err);
    emitStatus("error", `Sicherung fehlgeschlagen (${lastSaveError})`);
  }
}

export function getLastSaveError(): string | null {
  return lastSaveError;
}

function timeShort(): string {
  return new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}
